import React, { Typography, Grid, TextField, Button, Box, Autocomplete } from '@mui/material';
import { useForm, Controller, SubmitHandler, SubmitErrorHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Delete } from '@mui/icons-material';
import { CustomerService } from 'services/';
import { Customer } from 'models/';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth, usePartners } from 'hooks/';
import { CustomerFormProps } from './CustomerFormProps';

const customerSchema = z
	.object({
		id: z.number().optional(),
		email: z.string().email({ message: 'Invalid email format' }).optional(),
		phoneNumber: z.string().min(1, { message: 'Phone is required' }),
		partnerId: z.number().min(1, { message: 'Partner is required' }),
		firstName: z.string().min(1, { message: 'First name is required' }),
		lastName: z.string().min(1, { message: 'Last name is required' }),
		addressLine: z.string().min(1, { message: 'Address is required' }),
		zipCode: z.string().min(1, { message: 'Zip code is required' }),
		city: z.string().min(1, { message: 'City is required' }),
		country: z.string().min(1, { message: 'Country is required' }),
	})
	.strict();

export const CustomerForm = ({ row }: CustomerFormProps) => {
	const queryClient = useQueryClient();
	const { partners } = usePartners();
	const { currentUserId, isAdministrator } = useAuth();
	const isNew = (row?.id ?? 0) === 0;
	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<Customer>({
		defaultValues: isNew ? { 
			id:0,
			email: '',
			phoneNumber: '',
			partnerId: isAdministrator ? 0 : currentUserId, 
            firstName: '',
            lastName: '',
            addressLine: '',
			zipCode: '',
            city: '',
            country: '',
		 } : row,
		resolver: zodResolver(customerSchema),
		mode: 'onBlur',
	});

	const onSubmit: SubmitHandler<Customer> = async data => {
		if (data.id === 0) {
			await CustomerService.create(data);
		} else {
			await CustomerService.update(data);
		}
		queryClient.invalidateQueries({ queryKey: ['customers'] });
	};

	const onErrorSubmit: SubmitErrorHandler<Customer> = async e => {
		console.log(e);
	};

	const handleDeleteCustomer = async () => {
		if (window.confirm('Are you sure you want to delete this customer?')) {
			await CustomerService.delete(row?.id ?? 0);
			queryClient.invalidateQueries({ queryKey: ['customers'] });
		}
	};

	if (!partners) return <></>
	return (
		<Box sx={{ margin: 2 }} component='form' onSubmit={handleSubmit(onSubmit, onErrorSubmit)}>
			<Typography variant='h5' gutterBottom component='div'>
				{`${isNew ? 'Create' : 'Edit'} Customer`}
			</Typography>
			<Grid container spacing={2} p={1}>
				<Grid item xs={6}>
					<Controller
						name='email'
						control={control}
						render={({ field }) => <TextField {...field} label='Email' size='small' fullWidth required error={!!errors.email} helperText={errors.email?.message} />}
					/>
				</Grid>
				<Grid item xs={6}>
					<Controller
						name='phoneNumber'
						control={control}
						render={({ field }) => <TextField {...field} label='Phone' size='small' fullWidth required error={!!errors.phoneNumber} helperText={errors.phoneNumber?.message} />}
					/>
				</Grid>
				{isAdministrator && (
					<Grid item xs={12}>
						<Controller
							name='partnerId'
							control={control}
							render={({ field }) => (
								<Autocomplete
									value={partners?.find(partner => partner.id === field.value) || null}
									onChange={(_, value) => field.onChange(value?.id ?? 0)}
									options={partners}
									fullWidth
									getOptionLabel={option => option.firstName + ' ' + option.lastName}
									isOptionEqualToValue={(option) => option?.id === field?.value}
									renderInput={params => <TextField label='Partner' required error={!!errors.partnerId} helperText={errors.partnerId ? errors.partnerId.message : ''} {...params} size='small' />}
								/>
							)}
						/>
					</Grid>
				)}
				<Grid item xs={6}>
					<Controller
						name='firstName'
						control={control}
						render={({ field }) => <TextField {...field} label='First name' size='small' fullWidth required error={!!errors.firstName} helperText={errors.firstName?.message} />}
					/>
				</Grid>
				<Grid item xs={6}>
					<Controller
						name='lastName'
						control={control}
						render={({ field }) => <TextField {...field} label='Last name' size='small' fullWidth required error={!!errors.lastName} helperText={errors.lastName?.message} />}
					/>
				</Grid>
				<Grid item xs={6}>
					<Controller
						name='addressLine'
						control={control}
						render={({ field }) => <TextField {...field} label='Address' size='small' fullWidth required error={!!errors.addressLine} helperText={errors.addressLine?.message} />}
					/>
				</Grid>
				<Grid item xs={6}>
					<Controller
						name='zipCode'
						control={control}
						render={({ field }) => <TextField {...field} label='Zip code' size='small' fullWidth required error={!!errors.zipCode} helperText={errors.zipCode?.message} />}
					/>
				</Grid>
				<Grid item xs={6}>
					<Controller
						name='city'
						control={control}
						render={({ field }) => <TextField {...field} label='City' size='small' fullWidth required error={!!errors.city} helperText={errors.city?.message} />}
					/>
				</Grid>
				<Grid item xs={6}>
					<Controller
						name='country'
						control={control}
						render={({ field }) => <TextField {...field} label='Country' size='small' fullWidth required error={!!errors.country} helperText={errors.country?.message} />}
					/>
				</Grid>
				{isNew ? (
					<Grid item xs={12}>
						<Button type='submit' variant='contained' fullWidth color='primary'>
							Create
						</Button>
					</Grid>
				) : (
					<>
						<Grid item xs={10}>
							<Button type='submit' variant='contained' fullWidth color='primary'>
								Update
							</Button>
						</Grid>
						<Grid item xs={2}>
							<Button variant='contained' fullWidth color='error' onClick={handleDeleteCustomer}>
								<Delete />
							</Button>
						</Grid>
					</>
				)}
			</Grid>
		</Box>
	);
};
